<template>
  <div class="step">
    <!--  TITLE  -->
    <component
      :is="'question-base-radio-list'"
      question-id="title"
      product-name="home"
      step-id="insured"
      step-type="multiple"
      :step-index="1"
      :custom-fields="{ field: 'title', }"
      title="What is your title?"
      :help-text="{
        value: 'If your title is not in the list, please select the most appropriate for you.',
      }"
      :options-config=" {
        options: helpers.getTitleOptions(isMxDrLiveHome),
      }"
      :validation-rules=" {
        field: {
          required: true,
        },
      }"
      :validation-errors=" {
        field: {
          required: 'Please select a title.',
        },
      }"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
    />

    <!--  FIRSTNAME, SURNAME  -->
    <component
      :is="'question-name'"
      product-name="home"
      step-id="insured"
      question-id="name"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
    />

    <!--  DATE OF BIRTH  -->
    <component
      :is="'question-date-of-birth'"
      question-id="date-of-birth"
      product-name="home"
      class="date-of-birth"
      step-id="insured"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      :help-text="false"
      :columns-alignment="{
        day: {
          sm: '3',
          cols: '12',
        },
        month: {
          sm: '5',
          cols: '12',
        },
        year: {
          sm: '4',
          cols: '12',
        }
      }"
      @input="updateInsured"
    />

    <!--  POSTCODE CONTACT  -->
    <component
      :is="'question-postcode-contact'"
      question-id="postcode-contact"
      product-name="home"
      step-id="insured"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
    />

    <!--  MARITAL STATUS  -->
    <component
      :is="'question-marital-status'"
      product-name="home"
      step-id="insured"
      question-id="marital-status"
      :validation-errors=" {
        maritalStatus: {
          required: 'Please confirm your relationship status.',
        },
      }"
      :help-text="`
        <p>There might be several options that fit you - so choose the way that you would like to be described.</p>
        <p>Common law includes co-habiting/living with a partner.</p>
      `"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
    />

    <!--  EMPLOYMENT STATUS  -->
    <component
      :is="'question-employment-status'"
      product-name="home"
      step-id="insured"
      question-id="employment-status"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      :validation-errors="{
        employmentStatus: {
          is_not: 'Please select your employment status.',
        },
      }"
      @input="updateInsured"
    />

    <!--  OCCUPATION  -->
    <component
      :is="'question-occupation'"
      ref="occupation"
      product-name="home"
      step-id="insured"
      question-id="occupation"
      :subtitle="false"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
      @toast="$emit('toast', $event)"
    />

    <!--  BUSINESS  -->
    <component
      :is="'question-business'"
      product-name="home"
      step-id="insured"
      question-id="business"
      title="What is your business type?"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
      @toast="$emit('toast', $event)"
    />

    <!--  PART TIME OCCUPATION  -->
    <component
      :is="'question-another-job'"
      product-name="home"
      step-id="insured"
      question-id="another-job"
      title="Do you have a part-time occupation?"
      title-occupation="What is your part-time occupation?"
      title-business="For this job, what is your employer's business type?"
      is-highlighted
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
      @toast="$emit('toast', $event)"
    />

    <!--  TELEPHONE  -->
    <component
      :is="'question-telephone'"
      product-name="home"
      step-id="insured"
      question-id="telephone"
      title="What is your phone number?"
      :subtitle="false"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      :help-text="{
        value: isExperimentMinimiseHelptext && 'Your insurance provider may need to contact you to discuss your policy. Later in this form, we’ll also ask you if you want to share your number with the providers who offer you the lowest prices, to discuss your insurance needs before you buy (this is a free service and completely optional).',
        openerText: 'Why do I need to enter a phone number?',
        collapsible: isExperimentMinimiseHelptext,
        fullWidth: true
      }"
      @input="updateInsured"
    />

    <!--  EMAIL  -->
    <component
      :is="'question-email'"
      product-name="home"
      step-id="insured"
      question-id="email"
      title="What is your email address?"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
    />

    <!--  ADDITIONAL INSURED  -->
    <component
      :is="'question-additional-insured'"
      product-name="home"
      step-id="insured"
      question-id="additional-insured"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      :show-confirm-modal="showConfirmModal"
      @input="updateInsured"
      @reset-additional="resetAdditionalInsured"
      @save-additional="saveAdditionalInsured"
      @tracking="$emit('tracking', 'insured-additional-1')"
      @toast="$emit('toast', $event)"
    />

    <!--  PROPERTY BANKRUPT  -->
    <component
      :is="'question-base-radio-boolean'"
      question-id="property-bankrupt"
      product-name="home"
      step-id="insured"
      title="Have you, any proposed joint party, or anyone living at the property ever been bankrupt?"
      :custom-fields="{ field: 'propertyBankrupt' }"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
    />

    <!--  SPECIAL TERMS OF POLICY  -->
    <component
      :is="'question-base-radio-boolean'"
      question-id="special-terms-policy"
      product-name="home"
      step-id="insured"
      title="Have you or any occupant ever had any special terms imposed on previous insurance policies?"
      :help-text="{
        value: 'Your insurance provider would have notified you if you had any special terms imposed on your policy when you took out the policy or when it renewed. These are generally special terms outside of the usual standard policy terms. E.g. an increased excess for situations that have resulted in numerous claims, for example, escape of water or flooding.',
        openerText: 'Identifying Special Terms on Your Policy',
        collapsible: isExperimentMinimiseHelptext,
        fullWidth: true
      }"
      :custom-fields="{ field: 'specialTermsPolicy' }"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
    />

    <!--  POLICY DECLINED  -->
    <component
      :is="'question-base-radio-boolean'"
      question-id="policy-declined"
      product-name="home"
      step-id="insured"
      title="Have you or any other occupant ever had insurance declined, cancelled or voided?"
      :help-text="{
        value: '<p>Insurance providers might cancel an existing insurance policy for a variety of reasons, such as non-disclosure of material or important facts, the failure to disclose any unspent criminal convictions, or any form of misrepresentation by the proposer or any other person named on the policy.</p><p>Alternatively, an insurance provider might decline to renew your cover when an existing policy expires.</p><p>If you or any other occupant has ever had a previous insurance policy cancelled or declined in this way, you should answer ‘yes’ to this question.</p>',
        collapsible: true,
        openerText: isExperimentMinimiseHelptext ? 'Reasons for Insurance Policy Cancellation' : 'Show me more about this',
        closerText: isExperimentMinimiseHelptext ? 'Reasons for Insurance Policy Cancellation' : 'Hide',
        fullWidth: true,
      }"
      :custom-fields="{ field: 'policyDeclined' }"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
    />

    <!--  OCCUPANTS CONVICTIONS  -->
    <component
      :is="'question-base-radio-list'"
      question-id="occupants-convictions"
      product-name="home"
      step-id="insured"
      title="Do any occupants have any unspent criminal convictions or pending prosecutions?"
      :custom-fields="{ field: 'occupantsConvictions' }"
      :options-config="{
        options: constants.COVER_OCCUPANTS_CONVICTIONS
      }"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
    />

    <!--  CLAIMS  -->
    <component
      :is="'question-claims'"
      product-name="home"
      step-id="insured"
      question-id="claims"
      :show-confirm-modal="showConfirmModal"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      :is-experiment-minimise-helptext="isExperimentMinimiseHelptext"
      @input="updateInsured"
    />

    <!--  DATE OF INSURANCE  -->
    <component
      :is="'question-date-of-insurance-start'"
      product-name="home"
      step-id="insured"
      question-id="date-of-insurance-start"
      title="When should the insurance policy begin?"
      subtitle="Your cover start date must be within 30 days of today."
      :help-text="dateOfInsuranceStartHelpText"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
    />

    <!--  PROPERTY PAYMENT TYPE  -->
    <component
      :is="'question-base-radio-list'"
      question-id="property-payment-type"
      product-name="home"
      step-id="insured"
      title="How would you like to pay for the insurance policy?"
      :custom-fields="{ field: 'propertyPaymentType' }"
      :help-text="paymentTypeHelpText"
      :options-config="{
        options: constants.COVER_PROPERTY_PAYMENT_TYPE
      }"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      :validation-errors="{
        field: {
          required: 'Please confirm your preferred payment type.',
        },
      }"
      @input="updateInsured"
    />

    <!--  BUILD NO CLAIMS BONUS  -->
    <component
      :is="'question-base-dropdown'"
      v-if="isCoverTypeHadBuilding"
      question-id="build-no-claims-bonus"
      product-name="home"
      step-id="insured"
      title="How many years no claim discount do you have for buildings insurance?"
      :custom-fields="{ field: 'buildNoClaimsBonus' }"
      :help-text="{
        value: 'This is the number of years you have continuously held buildings insurance for without making a claim or loss. Continuous means not having a break from a policy for no longer than 30 days.',
        fullWidth: true,
        collapsible: true,
        openerText: isExperimentMinimiseHelptext ? 'Building Insurance History Explained' : 'Tell me more about no claims discount',
        closerText: isExperimentMinimiseHelptext ? 'Building Insurance History Explained' : 'Hide',
      }"
      :options-config="{
        options: constants.COVER_BUILDING_NO_CLAIMS_BONUS,
        placeholder: 'Please select'
      }"
      :validation-rules=" {
        field: { required: true },
      }"
      :validation-errors=" {
        field: {
          required: 'Please select no claims years.',
        },
      }"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
    />

    <!--  CONTENTS NO CLAIMS BONUS  -->
    <component
      :is="'question-base-dropdown'"
      v-if="isCoverTypeHadContents"
      question-id="contents-no-claims-bonus"
      product-name="home"
      step-id="insured"
      title="How many years no claim discount do you have for contents insurance?"
      :custom-fields="{ field: 'contentsNoClaimsBonus' }"
      :help-text="{
        value: 'This is the number of years you have continuously held contents insurance for without making a claim or loss. Continuous means not having a break from a policy for no longer than 30 days.',
        fullWidth: true,
        collapsible: true,
        openerText: isExperimentMinimiseHelptext ? 'Contents Insurance History Explained' : 'Tell me more about no claims discount',
        closerText: isExperimentMinimiseHelptext ? 'Contents Insurance History Explained' : 'Hide',
      }"
      :options-config="{
        options: constants.COVER_CONTENTS_NO_CLAIMS_BONUS,
        placeholder: 'Please select'
      }"
      :validation-rules=" {
        field: { required: true },
      }"
      :validation-errors=" {
        field: {
          required: 'Please select no claims years.',
        },
      }"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
    />

    <!--  BUILDING VOLUNTARY EXCESS  -->
    <component
      :is="'question-base-dropdown'"
      v-if="isCoverTypeHadBuilding"
      question-id="build-voluntary-excess"
      product-name="home"
      step-id="insured"
      title="How much voluntary excess would you like for your buildings cover?"
      :custom-fields="{ field: 'buildVoluntaryExcess' }"
      :help-text="{
        value: 'Select the amount which you will pay if you make a claim. A higher voluntary excess normally reduces your premium. This will be <b>in addition</b> to any compulsory excesses added onto the policy.',
        fullWidth: true,
        collapsible: true,
        openerText: isExperimentMinimiseHelptext ? 'Voluntary Excess and Premium Impact' : 'Tell me more about excesses',
        closerText: isExperimentMinimiseHelptext ? 'Voluntary Excess and Premium Impact' : 'Hide',
      }"
      :options-config="{
        options: constants.COVER_BUILD_VOLUNTARY_EXCESS
      }"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
    />

    <!--  CONTENTS VOLUNTARY EXCESS  -->
    <component
      :is="'question-base-dropdown'"
      v-if="isCoverTypeHadContents"
      question-id="contents-voluntary-excess"
      product-name="home"
      step-id="insured"
      title="How much voluntary excess would you like for your contents cover?"
      :custom-fields="{ field: 'contentsVoluntaryExcess' }"
      :help-text="{
        value: 'Select the amount which you will pay if you make a claim. A higher voluntary excess normally reduces your premium. This will be <b>in addition</b> to any compulsory excesses added onto the policy.',
        fullWidth: true,
        collapsible: true,
        openerText: isExperimentMinimiseHelptext ? 'Voluntary Excess and Premium Impact' : 'Tell me more about excesses',
        closerText: isExperimentMinimiseHelptext ? 'Voluntary Excess and Premium Impact' : 'Hide',
      }"
      :options-config="{
        options: constants.COVER_BUILD_VOLUNTARY_EXCESS
      }"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
    />

    <!--  BUILDING ACCIDENTAL DAMAGE  -->
    <component
      :is="'question-base-radio-boolean'"
      v-if="isCoverTypeHadBuilding"
      question-id="build-acc-dam"
      product-name="home"
      step-id="insured"
      title="Do you wish to cover your building against accidental damage?"
      :custom-fields="{ field: 'buildAccDam' }"
      :help-text="buildingAccidentalDamageHelpText"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
    />

    <!--  CONTENTS ACCIDENTAL DAMAGE  -->
    <component
      :is="'question-base-radio-boolean'"
      v-if="isCoverTypeHadContents"
      question-id="contents-acc-dam"
      product-name="home"
      step-id="insured"
      title="Do you require accidental damage cover for your contents?"
      :custom-fields="{ field: 'contentsAccDam' }"
      :help-text="contentsAccidentalDamageHelpText"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
    />

    <!-- PASSWORD -->
    <component
      :is="'question-password'"
      product-name="home"
      step-id="insured"
      question-id="user-data"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
    />

    <!-- OUTBOUND -->
    <component
      :is="'question-outbound'"
      question-id="outbound"
      step-id="insured"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
    />

    <!-- DIRECT/PARTNER MARKETING OPTIONS -->
    <component
      :is="'question-direct-marketing-options'"
      question-id="direct-marketing-options"
      product-name="home"
      step-id="insured"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      @input="updateInsured"
    />

    <!-- USER KEEP SIGN IN -->
    <component
      :is="'question-user-keep-sign-in'"
      question-id="user-keep-sign-in"
      product-name="home"
      step-id="insured"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="insured"
      :default-values="{
        userKeepSignIn: true
      }"
      @input="updateInsured"
    />

    <!-- TERMS AND CONDITIONS -->
    <form-section no-validity>
      <form-section-question>Almost there... a few final things before we generate your quotes.</form-section-question>

      <form-section-label>Terms & Conditions</form-section-label>

      <div
        class="terms-and-conditions ql-editor"
        data-test="terms_and_conditions"
        v-html="config.partnerTermsAndConditions"
      />
    </form-section>
  </div>
</template>

<script>
import QuestionPostcodeContact from '@/components/questions/home/PostcodeContact';
import QuestionClaims from '@/components/questions/home/Claims';
import QuestionAdditionalInsured from '@/components/questions/home/AdditionalInsured';
import QuestionPassword from '@/components/questions/Password';

import { mapActions } from 'vuex';

import {
  cloneDeep,
} from 'lodash';
import { inject } from '@vue/composition-api';

import {
  INSURED,
} from '@/payloadTemplates/home';
import useExperimentMinimiseHelptext from '@/composables/useExperimentMinimiseHelptext';

export default {
  name: 'form-home-step-3',
  components: {
    QuestionPostcodeContact,
    QuestionClaims,
    QuestionAdditionalInsured,
    QuestionPassword,
  },
  props: {
    formObserver: {
      type: Object,
      required: true,
    },
    form: {
      type: Object,
      required: true,
    },
  },
  inject: [
    'config',
    'constants',
    'helpers',
  ],
  data () {
    return {
      step: null,
    };
  },
  computed: {
    paymentTypeHelpText () {
      const helpTextValue = [
        '<p>Paying via monthly instalments allows you to spread the cost over the year, but you may end up paying more in total.</p>',
        '<p>Paying annually may mean you pay less overall for the policy by avoiding any credit fees.</p>',
      ];
      return {
        value: this.isExperimentMinimiseHelptext ? helpTextValue.join('') : helpTextValue,
        collapsible: this.isExperimentMinimiseHelptext,
        openerText: 'Monthly vs. Annual Payments',
        fullWidth: true,
      };
    },
    dateOfInsuranceStartHelpText () {
      const helpText = 'Prices from some insurance providers will often be lower further from your renewal date, compared to the prices in the few days leading up to your renewal. It’s good to make sure there are no gaps in your cover, so you’re always insured.</p>';
      const helpTextTitle = '<p><strong>Good to know</strong><br>';
      return {
        value: this.isExperimentMinimiseHelptext ? helpText : helpTextTitle + helpText,
        collapsible: this.isExperimentMinimiseHelptext,
        openerText: 'Renew Early to Lower Insurance Costs',
        fullWidth: true,
      };
    },
    buildingAccidentalDamageHelpText () {
      const helpTextValue = [
        '<p>This provides cover for accidental damage to your property for unintentional or accidental incidents e.g. damaging cables or pipes whilst hammering a nail into a wall.</p>',
        '<p>Please check your policy details with the insurance provider for full details of this cover.</p>',
      ];
      return {
        value: this.isExperimentMinimiseHelptext ? helpTextValue.join('') : helpTextValue,
        collapsible: this.isExperimentMinimiseHelptext,
        openerText: 'Accidental Damage Coverage Details',
        fullWidth: true,
      };
    },
    contentsAccidentalDamageHelpText () {
      const helpTextValue = [
        '<p>This provides cover for accidental damage to your contents for unintentional or accidental incidents e.g. breaking a vase  or you hang your wall mounted TV wrong and it falls and breaks.</p>',
        '<p>Please check your policy details with the insurance provider for full details of this cover.</p>',
      ];
      return {
        value: this.isExperimentMinimiseHelptext ? helpTextValue.join('') : helpTextValue,
        collapsible: this.isExperimentMinimiseHelptext,
        openerText: 'Accidental Damage Coverage Details',
        fullWidth: true,
      };
    },
    insured: {
      get () {
        return this.step[0];
      },
      set (value) {
        this.$set(this.step, 0, { ...this.step[0], ...value });
      },
    },
    home () {
      return this.form.home;
    },

    isCoverTypeHadContents () {
      const type = this.home.propertyCoverType;
      return type === 'BC' || type === 'C';
    },
    isCoverTypeHadBuilding () {
      const type = this.home.propertyCoverType;
      return type === 'BC' || type === 'B';
    },
  },
  watch: {
    step: {
      handler (val) {
        this.$emit('step-data', val);
      },
      immediate: true,
    },
  },
  created () {
    this.$emit('tracking', 'insured');
    this.initStep();
  },
  methods: {
    ...mapActions(['showConfirmModal']),
    // init
    initStep () {
      this.step = cloneDeep(this.form.insured);

      // reset step building hidden questions
      if (!this.isCoverTypeHadBuilding) {
        this.insured.buildNoClaimsBonus = INSURED.buildNoClaimsBonus;
        this.insured.buildVoluntaryExcess = INSURED.buildVoluntaryExcess;
        this.insured.buildAccDam = INSURED.buildAccDam;
      }

      // reset step contents hidden questions
      if (!this.isCoverTypeHadContents) {
        this.insured.contentsNoClaimsBonus = INSURED.contentsNoClaimsBonus;
        this.insured.contentsVoluntaryExcess = INSURED.contentsVoluntaryExcess;
        this.insured.contentsAccDam = INSURED.contentsAccDam;
      }
    },

    updateInsured (questionData) {
      for (const key in questionData) {
        this.insured[key] = questionData[key];
      }
    },

    // additional insured
    resetAdditionalInsured () {
      this.step.splice(1, 1);
    },
    saveAdditionalInsured ({ insured, isUpdate }) {
      if (isUpdate) {
        this.$set(this.step, 1, insured);
      } else {
        this.step.push(insured);
      }
    },
  },
  setup () {
    const { isMxDrLiveHome } = inject('abTastyControllerUI');

    // COMPOSABLES
    const { isExperimentMinimiseHelptext } = useExperimentMinimiseHelptext();

    return {
      isMxDrLiveHome,
      isExperimentMinimiseHelptext,
    };
  },
};
</script>

<style lang="scss" scoped>
.date-of-birth::v-deep {
  .form-col {
    width: 100%;

    .form-row-title {
      margin-bottom: 12px;
    }
  }
}

.small-title {
  font-size: 18px;

  $fzlh_ratio: 36 / 28;

  width: 100%;
  text-align: left;
  line-height: var(--lh, calc(var(--fz, 28px) * #{$fzlh_ratio}));
  font-weight: var(--font-1-weight, bold);
  font-family: var(--font-1);
  color: var(--v-navy-dark-base, $clr-navy-dark);

  @media screen and (max-width: 767px) {
    margin-top: 16px;
    padding-right: 30px;
    margin-bottom: 24px;
  }
}

.postcode {
  .form-row {
    @media screen and (max-width: 1199px) {
      flex-direction: column;
    }
  }

  &::v-deep .form-col {
    @media screen and (max-width: 1199px) {
      width: 100% !important;

      & + .form-col {
        margin-top: 23px !important;
      }
    }
  }
}

.postcode-contact-control {
  @media screen and (max-width: 480px) {
    width: 100%;

    div {
      width: 100%;
    }

    .postcode-wrap {
      flex-direction: column;
      width: 100%;

      .v-input {
        margin-right: 0 !important;
      }

      button {
        width: 100%;
        height: 56px !important;
        margin-top: 16px;
      }
    }
  }
}
</style>

<style lang='scss'>
.wide-title {
  .form-col:first-of-type {
    width: 100%;
  }
}
</style>
